import { apiFetchRussian } from './api';

export type AuthCapabilities = {
  isAuthenticated: boolean;
  isReviewer: boolean;
  isSuperuser: boolean;
  username: string | null;
};

export type InternalCorrectionStatus =
  | 'SUBMITTED'
  | 'AWAITING_SECOND_REVIEW'
  | 'APPROVED_AWAITING_RECALC'
  | 'REJECTED'
  | 'DISPUTED';

export type ModerationActions = {
  canFirstReview: boolean;
  canSecondReview: boolean;
  canResolve: boolean;
  blockedReason: 'CONFLICT_OF_INTEREST' | 'ALREADY_REVIEWED' | null;
};

export type ModerationQueueItem = {
  public_id: string;
  status: InternalCorrectionStatus;
  created_at: string;
  game_date: string;
  replay_name: string;
  replay_url: string;
  claimed_killer_name: string;
  claimed_killer_squad: string;
  claimed_killer_is_solo: boolean;
  claimed_victim_name: string;
  claimed_victim_squad: string;
  claimed_victim_is_solo: boolean;
  actions: ModerationActions;
};

export type ModerationReview = {
  stage: 'FIRST' | 'SECOND' | 'RESOLUTION';
  decision: 'APPROVE' | 'REJECT' | 'CONFIRM' | 'DISPUTE';
  reviewer_label: 'Модератор 1' | 'Модератор 2' | 'Модератор 3';
  proposal_hash: string;
  comment: string;
  public_rejection_reason_snapshot: string | null;
  created_at: string;
};

export type ModerationProposal = {
  k_squad: string;
  k_is_solo: boolean;
  k_name: string;
  w_class: string;
  distance: number;
  v_name: string;
  v_squad: string;
  v_is_solo: boolean;
  tk: 0 | 1;
  wk: 0 | 1;
  ak: 0 | 1;
  replay_date: string;
  replay_name: string;
  type_w: string;
  payload_hash: string;
};

export type ModerationDetail = ModerationQueueItem & {
  updated_at: string;
  event_time_seconds: number;
  claimed_weapon_or_method: string;
  claimed_distance: number | null;
  description: string;
  public_rejection_reason: string | null;
  evidence: Array<{ url: string; label: string }>;
  reviews: ModerationReview[];
  proposal?: ModerationProposal;
  published_manual_entry_id: number | null;
};

export type PaginatedModerationQueue = {
  count: number;
  next: string | null;
  previous: string | null;
  results: ModerationQueueItem[];
};

export type ModerationSummary = {
  awaitingRecalcCount: number;
};

export type ModerationQueueFilters = {
  page?: number;
  status?: InternalCorrectionStatus | '';
  gameDate?: string;
  replay?: string;
  squad?: string;
  pageSize?: number;
};

export type ProposalInput = {
  k_squad: string;
  k_is_solo: boolean;
  k_name: string;
  w_class: string;
  distance: number;
  v_name: string;
  v_squad: string;
  v_is_solo: boolean;
  tk: 0 | 1;
};

export type WeaponOption = {
  className: string;
  type: string;
  wk: 0 | 1;
  ak: 0 | 1;
};

const correctionPath = (publicId: string, suffix = '') =>
  `/moderation/manual-corrections/${encodeURIComponent(publicId)}/${suffix}`;

export const getAuthCapabilities = (): Promise<AuthCapabilities> =>
  apiFetchRussian<AuthCapabilities>('/auth/me/');

export const loginModerator = (username: string, password: string): Promise<AuthCapabilities> =>
  apiFetchRussian<AuthCapabilities>('/auth/login/', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });

export const logoutModerator = (): Promise<void> =>
  apiFetchRussian<void>('/auth/logout/', { method: 'POST' });

export const listModerationQueue = (
  filters: ModerationQueueFilters = {}
): Promise<PaginatedModerationQueue> => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.set(key, String(value));
  });
  const query = params.toString();
  return apiFetchRussian<PaginatedModerationQueue>(
    `/moderation/manual-corrections/${query ? `?${query}` : ''}`
  );
};

export const getModerationDetail = (publicId: string): Promise<ModerationDetail> =>
  apiFetchRussian<ModerationDetail>(correctionPath(publicId));

export const getModerationSummary = (): Promise<ModerationSummary> =>
  apiFetchRussian<ModerationSummary>('/moderation/manual-corrections/summary/');

export const searchWeaponOptions = (query: string): Promise<{ results: WeaponOption[] }> =>
  apiFetchRussian<{ results: WeaponOption[] }>(
    `/moderation/manual-corrections/options/weapons/?q=${encodeURIComponent(query.trim())}`
  );

export const submitFirstReview = (
  publicId: string,
  payload:
    | { decision: 'APPROVE'; comment: string; proposal: ProposalInput }
    | { decision: 'REJECT'; comment: string; public_reason: string | null }
): Promise<ModerationDetail> =>
  apiFetchRussian<ModerationDetail>(correctionPath(publicId, 'first-review/'), {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const submitSecondReview = (
  publicId: string,
  payload: { decision: 'CONFIRM' | 'DISPUTE'; comment: string; proposal_hash: string }
): Promise<ModerationDetail> =>
  apiFetchRussian<ModerationDetail>(correctionPath(publicId, 'second-review/'), {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const resolveDispute = (
  publicId: string,
  payload:
    | { decision: 'APPROVE'; comment: string; proposal?: ProposalInput }
    | { decision: 'REJECT'; comment: string; public_reason: string | null }
): Promise<ModerationDetail> =>
  apiFetchRussian<ModerationDetail>(correctionPath(publicId, 'resolve/'), {
    method: 'POST',
    body: JSON.stringify(payload),
  });
